/**
 * `<SignInPage>` — the in-app sign-in surface, rendered at `/sign-in/*`.
 *
 * Embeds Clerk's React `<SignIn />` component INSIDE our app shell (slate
 * background, marketing-style hero copy + brand wordmark) instead of using
 * Clerk's hosted page. This way the visual experience matches the marketing
 * site pixel-for-pixel — Clerk's component handles the form mechanics + OAuth
 * provider buttons + verification flow, but our CSS owns the chrome.
 *
 * `appearance` prop maps every Clerk-rendered element to our design tokens via
 * the `elements` keyed-class API. The colors come from the slate palette that
 * `auth-surface.css` pins locally on `.auth-surface` (NOT the visitor's app
 * theme — see the PALETTE note there) — the same OKLCH palette the marketing
 * site uses, so a user signing up here sees the exact same slate surface +
 * liquid-oxygen-blue + nixie-orange + IBM-Plex-Mono-eyebrow language they saw
 * on the landing page.
 *
 * Clerk auto-handles:
 *   • Email + password
 *   • Email verification (six-digit code emailed at signup)
 *   • Password reset (Forgot password? flow)
 *   • OAuth providers (Google / GitHub / Microsoft / etc.) once configured in
 *     the Clerk dashboard. Buttons appear automatically — no code change here
 *     when we toggle Google later.
 *
 * Routing: Clerk's component has its own internal multi-step routing for the
 * signup flow (form → verification → factor selection). The parent route
 * pattern is `/sign-in/*` so React Router defers all sub-paths to Clerk.
 */

import { SignIn } from '@clerk/clerk-react';
import './auth-surface.css';

export function SignInPage(): JSX.Element {
  return (
    <div className="auth-surface">
      <a href="https://aletheiapkm.com" className="auth-surface__brand" aria-label="Aletheia home">
        <span className="auth-surface__name">aletheia</span>
      </a>

      <div className="auth-surface__hero">
        <div className="auth-surface__eyebrow">
          welcome back <span className="tick">·</span> your workspace
        </div>
        <h1 className="auth-surface__title">
          Your knowledge, <em>right where you left it.</em>
        </h1>
        <p className="auth-surface__sub">
          Sign in to pick up where you left off — <strong>your library, your
          connections, and every answer you’ve built</strong>, ready when you are.
        </p>
      </div>

      <SignIn
        path="/sign-in"
        routing="path"
        signUpUrl="/sign-up"
        // Match the marketing-site slate palette. The auth-clerk-* element
        // classes reference CSS variables that .auth-surface pins to slate, so
        // the widget stays slate regardless of the visitor's app theme.
        appearance={CLERK_APPEARANCE}
      />
    </div>
  );
}

/**
 * Shared Clerk appearance config — used by both <SignInPage> and <SignUpPage>
 * so they look identical. Lifted from the v2 marketing-site index.html's
 * exact Slate+Gold token set (colors, spacing, radii, fonts). Clerk's
 * `appearance.variables` doesn't read CSS custom properties — values must be
 * literal color strings, so these are kept in sync by hand with the slate
 * theme in tokens.css. If you change the palette there, change these too.
 */
export const CLERK_APPEARANCE = {
  variables: {
    // Color tokens (v2 Slate, pale-oxygen-blue primary)
    colorPrimary: 'oklch(0.86 0.055 215)',           // --primary (pale oxygen blue)
    colorBackground: 'oklch(0.180 0.004 260)',       // --bg-1
    colorInputBackground: 'oklch(0.215 0.005 260)',  // --bg-2
    colorInputText: 'oklch(0.965 0.006 250)',        // --fg
    colorText: 'oklch(0.965 0.006 250)',             // --fg
    colorTextSecondary: 'oklch(0.78 0.010 250)',     // --fg-2
    colorNeutral: 'oklch(0.30 0.005 260)',           // --line
    colorDanger: 'oklch(0.68 0.15 25)',              // --bad
    colorSuccess: 'oklch(0.74 0.12 148)',            // --good
    colorWarning: 'oklch(0.78 0.10 82)',             // --warn
    // Typography
    fontFamily: '"IBM Plex Sans", "Source Sans 3", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontFamilyButtons: '"IBM Plex Sans", "Source Sans 3", -apple-system, sans-serif',
    fontSize: '14.5px',
    fontWeight: { normal: '400', medium: '500', semibold: '600', bold: '700' },
    // Shape
    borderRadius: '6px',
  },
  elements: {
    // The outer card containing the form — match the marketing-site card style
    rootBox: 'auth-clerk-root',
    card: 'auth-clerk-card',
    // Header (Clerk's "Sign in to Aletheia PKM" line) — we render our own
    // hero above, so hide Clerk's header.
    headerTitle: 'auth-clerk-hide',
    headerSubtitle: 'auth-clerk-hide',
    // The social provider button row
    socialButtonsBlockButton: 'auth-clerk-social-btn',
    socialButtonsBlockButtonText: 'auth-clerk-social-btn-text',
    socialButtonsProviderIcon: 'auth-clerk-social-icon',
    // Divider between social + email
    dividerLine: 'auth-clerk-divider-line',
    dividerText: 'auth-clerk-divider-text',
    // Form field labels (Clerk's "Email address" "Password" labels)
    formFieldLabel: 'auth-clerk-label',
    // Form field inputs
    formFieldInput: 'auth-clerk-input',
    // The primary "Continue" submit button
    formButtonPrimary: 'auth-clerk-primary-btn',
    // Footer links ("Don't have an account? Sign up" etc.)
    footerActionLink: 'auth-clerk-link',
    footerActionText: 'auth-clerk-footer-text',
    // The "or" text between primary and secondary actions
    formFieldHintText: 'auth-clerk-hint',
    formFieldErrorText: 'auth-clerk-error',
    // The verification-code input grid
    otpCodeFieldInput: 'auth-clerk-otp-input',
    // Powered by Clerk badge — keep visible (their TOS requires) but small
    badge: 'auth-clerk-badge',
  },
} as const;
